import type { DashboardProject } from "../../types.js";
import type { ViewerProjectState } from "../../../../types/viewerDeployment.js";

export const PROJECTS_TAB_OPTIONS = [
  { id: "all", label: "All Projects" },
  { id: "active", label: "Active" },
  { id: "staged", label: "Staged" },
  { id: "completed", label: "Completed" },
] as const;

export type ProjectsTabId = (typeof PROJECTS_TAB_OPTIONS)[number]["id"];

export interface DashboardProjectRecordDto {
  id: string;
  title: string;
  companyName: string;
  updatedAtLabel: string;
  status: DashboardProject["status"];
  collaborators: string[];
  createdAtIso?: string | null;
  createdAtLabel?: string | null;
  animatorAssignedLabel?: string | null;
  companyId?: string | null;
  productId?: string | null;
}

export interface DashboardProjectCreateRequestDto {
  title: string;
  companyName: string;
  collaborators?: string[];
  entitlementId?: string | null;
}

export interface DashboardProjectCreateMutationResultDto {
  item: DashboardProjectRecordDto;
}

export interface DashboardProjectDetailResultDto {
  item: DashboardProjectRecordDto;
}

export interface DashboardProjectStatusUpdateMutationResultDto {
  item: DashboardProjectRecordDto;
}

export interface DashboardProjectsListDto {
  items: DashboardProjectRecordDto[];
}

export interface DashboardProjectArchiveDownloadResultDto {
  fileName: string;
}

export interface DashboardWorkspaceSnapshotDto {
  status: "loading" | "ready" | "error";
  error: string | null;
  companyId: string;
  productId: string;
  companyName: string;
  projectName: string;
  backendState: ViewerProjectState | null;
  previewUrl: string | null;
  liveUrl: string | null;
  draftRevision: number | null;
  draftStepCount: number | null;
  hasLiveDeployment: boolean;
  updatedAtLabel: string | null;
}

export interface ProjectsSummary {
  total: number;
  active: number;
  completed: number;
  staged: number;
}

export interface ProjectsWorkspaceSnapshotViewModel
  extends DashboardWorkspaceSnapshotDto {
  stateLabel: string;
}

const PROJECT_STATUS_VALUES: ReadonlySet<DashboardProject["status"]> = new Set([
  "in_production",
  "awaiting_review",
  "revision_requested",
  "waiting_on_client",
  "approved",
  "delivered",
  "blocked",
]);

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === "string");
}

function isNullableString(value: unknown): value is string | null | undefined {
  return value == null || typeof value === "string";
}

export function isDashboardProjectRecordDto(
  value: unknown,
): value is DashboardProjectRecordDto {
  if (!value || typeof value !== "object") {
    return false;
  }

  const record = value as Partial<DashboardProjectRecordDto>;
  return (
    isNonEmptyString(record.id) &&
    isNonEmptyString(record.title) &&
    isNonEmptyString(record.companyName) &&
    isNonEmptyString(record.updatedAtLabel) &&
    typeof record.status === "string" &&
    PROJECT_STATUS_VALUES.has(record.status as DashboardProject["status"]) &&
    isStringArray(record.collaborators) &&
    isNullableString(record.createdAtIso) &&
    isNullableString(record.createdAtLabel) &&
    isNullableString(record.animatorAssignedLabel) &&
    isNullableString(record.companyId) &&
    isNullableString(record.productId)
  );
}

export function validateDashboardProjectsListDto(
  value: unknown,
): value is DashboardProjectsListDto {
  if (!value || typeof value !== "object") {
    return false;
  }

  const payload = value as Partial<DashboardProjectsListDto>;
  return (
    Array.isArray(payload.items) &&
    payload.items.every((item) => isDashboardProjectRecordDto(item))
  );
}

export function validateDashboardProjectCreateMutationResultDto(
  value: unknown,
): value is DashboardProjectCreateMutationResultDto {
  if (!value || typeof value !== "object") {
    return false;
  }

  const payload = value as Partial<DashboardProjectCreateMutationResultDto>;
  return isDashboardProjectRecordDto(payload.item);
}

export function validateDashboardProjectDetailResultDto(
  value: unknown,
): value is DashboardProjectDetailResultDto {
  if (!value || typeof value !== "object") {
    return false;
  }

  const payload = value as Partial<DashboardProjectDetailResultDto>;
  return isDashboardProjectRecordDto(payload.item);
}

export function validateDashboardProjectStatusUpdateMutationResultDto(
  value: unknown,
): value is DashboardProjectStatusUpdateMutationResultDto {
  if (!value || typeof value !== "object") {
    return false;
  }

  const payload = value as Partial<DashboardProjectStatusUpdateMutationResultDto>;
  return isDashboardProjectRecordDto(payload.item);
}
